import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { storage } from '@/lib/storage';
import { useSettingsStore } from '@/api/bi/settings';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyDocumentTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    return storage.getTheme() ?? 'dark';
  });

  /** Push theme to the server-backed blob only on real user intent — never
   * from a bare `useEffect([theme])`, which would fire every mount and
   * cascade `applyServerBlob` → `notify` → saved-views updates in a tight
   * loop with Radix ref reconciliation ("Maximum update depth exceeded"). */
  const syncThemeToServer = useCallback((t: Theme) => {
    try {
      if (!useSettingsStore.getState().hydrated) return;
      if (useSettingsStore.getState().blob.theme === t) return;
      useSettingsStore.getState().patchLocal({ theme: t });
    } catch {
      /* noop — settings store may not be ready (logged-out boot, tests). */
    }
  }, []);

  const setTheme = useCallback(
    (t: Theme) => {
      setThemeState(t);
      syncThemeToServer(t);
    },
    [syncThemeToServer],
  );

  const toggle = useCallback(() => {
    setThemeState((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      queueMicrotask(() => syncThemeToServer(next));
      return next;
    });
  }, [syncThemeToServer]);

  useEffect(() => {
    applyDocumentTheme(theme);
    storage.setTheme(theme);
  }, [theme]);

  // After the post-login bootstrap GET resolves, adopt the server's `theme`
  // exactly once. Without this the very first paint sticks (cold-boot
  // `useState` reads the *base namespace* before `setStorageUser(sub)` runs,
  // so the user's saved 'light' silently reverts to 'dark' on every refresh).
  useEffect(() => {
    let appliedOnce = false;
    const unsub = useSettingsStore.subscribe((s) => {
      if (appliedOnce) return;
      if (!s.hydrated) return;
      appliedOnce = true;
      const fromServer = s.blob.theme;
      if (fromServer === 'light' || fromServer === 'dark') {
        setThemeState((cur) => (cur === fromServer ? cur : fromServer));
      }
    });
    // Catch the case where the store was already hydrated before we subscribed.
    const snap = useSettingsStore.getState();
    if (snap.hydrated && !appliedOnce) {
      appliedOnce = true;
      const fromServer = snap.blob.theme;
      if (fromServer === 'light' || fromServer === 'dark') {
        setThemeState((cur) => (cur === fromServer ? cur : fromServer));
      }
    }
    return unsub;
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        toggle,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
